import { runWithErrorReporting } from "$root/api/error-reporting";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import GraphLegend from "$root/components/GraphLegend";
import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useQuery } from "@tanstack/react-query";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { formatDate } from "$root/localization/formatters";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	portfolioWidgetMissingDataReason,
	WidgetStatus,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { Intercom } from "$root/third-party-integrations/initIntercom";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { BarGraphPCSvg } from "$root/ui-lib/charts";
import { commonColumns } from "$root/ui-lib/common-table-columns";
import {
	type ContextContent,
	downloadContentDisposition,
	getGraphMarkers,
	qualifier,
	roundCustomByStep,
	withContext,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import type { FactorsDto, InvestmentFactorsResponse } from "@eu.mdotm.api/sphere";
import {
	type OrderBy,
	type TableColumn,
	ActionText,
	AutoSortHScrollTable,
	AutoTooltip,
	defaultRowHeight,
	Row,
	TableDataCell,
	Text,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import { builtInSort } from "@mdotm/shadcn/legacy/utils";
import { DropdownMenu, Switch, Button } from "@mdotm/shadcn/mdotm/components";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildWidgetExportConfig, type FactorsExposureOrderBy } from "../widget-export";

type ReducedResultProps<T> = {
	[key: string]: {
		[key in keyof T]?: FactorsDto;
	} & { groupKey: string };
};

type Unpacked<T> = T extends (infer U)[] ? U : T;

type orderOptionsT = "PortfolioProposal" | "PortfolioBenchmark" | "ProposalBenchmark" | "Default";

/** Maps the widget's OrderBy selection to the data provider's `orderBy` enum used by the export. */
const ORDER_BY_BY_OPTION: Record<orderOptionsT, FactorsExposureOrderBy> = {
	Default: "BETA",
	PortfolioProposal: "PORTFOLIO_VS_PROPOSAL",
	PortfolioBenchmark: "PORTFOLIO_VS_BENCHMARK",
	ProposalBenchmark: "PROPOSAL_VS_BENCHMARK",
};

const PortfolioFactorsExposure = (props: ContextContent<typeof PortfolioContext>) => {
	const { portfolio, selectedBenchmark, enhanced, asOf, asOfSnapshot } = props;
	const uuid = portfolio?.uuid;
	const benchmarkId = selectedBenchmark ?? portfolio?.primaryBenchmarkIdentifier ?? "";
	const { t } = useTranslation();

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIOFACTORSEXPOSURE.TITLE"),
			tooltip: (
				<TooltipContent>
					<Text type="Body/M/Book">
						Gain insights into the performance drivers of the asset class through factor exposure analysis. Discover how
						Sphere calculates its factors by accessing more information &nbsp;
					</Text>
					<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("marketRegimeAnalysysTool")}>
						here
					</ActionText>
				</TooltipContent>
			),
		}),
		[t],
	);

	const queryRaw = useQuery({
		...portfolioRepository.factorsExposure.getQueryConfig(portfolio?.uuid ?? "", { enhanced, benchmarkId, asOf }),
		enabled: isAsOfReadable(asOf, asOfSnapshot),
	});
	const query = useMappedQuery(
		(factors): PortfolioWidgetData<InvestmentFactorsResponse | undefined> => {
			if (!uuid || !benchmarkId) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
				};
			}

			if (enhanced && !factors.current && !factors.benchmark && !factors.proposal) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
				};
			}

			if (!enhanced && !factors.current && !factors.benchmark) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "ExanteContributionVolatility"),
				};
			}

			// Factor lists that all arrive empty aggregate to no row at all.
			if ([factors.current, factors.benchmark, factors.proposal].every((list) => (list?.length ?? 0) === 0)) {
				return {
					data: undefined,
					widgetStatus: WidgetStatus.EMPTY,
				};
			}

			return {
				data: factors,
				widgetStatus: WidgetStatus.READY,
			};
		},
		[benchmarkId, enhanced, props.portfolio, uuid],
		queryRaw,
	);

	return (
		<PortfolioQueryWidgetBase query={query}>
			{(investmentFactors) => <PortfolioFactorsExposureInner ctx={props} investmentFactors={investmentFactors} />}
		</PortfolioQueryWidgetBase>
	);
};

const PortfolioFactorsExposureInner = ({
	ctx,
	investmentFactors,
}: {
	ctx: ContextContent<typeof PortfolioContext>;
	investmentFactors: InvestmentFactorsResponse;
}) => {
	const { enhanced, portfolio, selectedBenchmark } = ctx;
	const uuid = portfolio.uuid;
	const benchmarkId = selectedBenchmark ?? portfolio?.primaryBenchmarkIdentifier ?? "";

	const [initialOrder, setInitialOrder] = useState<orderOptionsT>("Default");
	const [filterBySignificant, setFilterBySignificant] = useState(true);

	// Mirror the table's column sort so the export reproduces it. Defaults to none (the provider orders
	// via the OrderBy buttons below); once the user sorts a column the export carries that same sort.
	const [orderBy, setOrderBy] = useState<OrderBy[]>([]);

	const { t } = useTranslation();

	const customObjectEntriesFn = Object.entries as <T, K extends keyof T>(obj: T) => Array<[K, T[K]]>;
	const customObjectValuesFn = Object.values as <T, K extends keyof T>(obj: T) => Array<T[K]>;

	const factorsDescription = useMemo(
		() =>
			t("EXPOSUREFACTORS.TOOLTIPDESCRIPTIONS", {
				returnObjects: true,
			}),
		[t],
	);

	const orderOptions: { label: string; value: orderOptionsT; onlyEnhance: boolean; show: boolean }[] = useMemo(
		() => [
			{ label: "Beta", value: "Default", onlyEnhance: false, show: true },
			{ label: "Portfolio vs proposal", value: "PortfolioProposal", onlyEnhance: true, show: true },
			{ label: "Portfolio vs benchmark", value: "PortfolioBenchmark", onlyEnhance: false, show: true },
			{ label: "Proposal vs benchmark", value: "ProposalBenchmark", onlyEnhance: true, show: true },
		],
		[],
	);

	// Download — mirrors the visible table via the smart-workspace `investmentFactor` data provider.
	const exportFactorsExposure = async (investmentUuid: string, benchmarkIdentifier: string) => {
		const config = buildWidgetExportConfig(
			"PortfolioFactorsExposure",
			{
				investmentUuid,
				benchmarkIdentifier,
				onlySignificant: filterBySignificant,
				enhanced,
				source: enhanced ? "PROPOSAL" : "CURRENT",
				orderBy: ORDER_BY_BY_OPTION[initialOrder],
				proposalDate: ctx.portfolio.modificationDate,
				sort: orderBy,
			},
			{ asOf: ctx.asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export investment factors exposure",
					payload: JSON.stringify(config),
				},
			},
		);
	};

	const exportFactorsExposureRef = useUnsafeUpdatedRef(exportFactorsExposure);

	const customOrderFn = useCallback(
		(a: NonNullable<typeof factorsData>[number], b: NonNullable<typeof factorsData>[number]) => {
			switch (initialOrder) {
				case "PortfolioProposal":
					return (
						Math.abs((b.current?.value ?? 0) - (b.proposal?.value ?? 0)) -
						Math.abs((a.current?.value ?? 0) - (a.proposal?.value ?? 0))
					);
				case "PortfolioBenchmark":
					return (
						Math.abs((b.current?.value ?? 0) - (b.benchmark?.value ?? 0)) -
						Math.abs((a.current?.value ?? 0) - (a.benchmark?.value ?? 0))
					);
				case "ProposalBenchmark":
					return (
						Math.abs((b.proposal?.value ?? 0) - (b.benchmark?.value ?? 0)) -
						Math.abs((a.proposal?.value ?? 0) - (a.benchmark?.value ?? 0))
					);
				default:
					return Math.abs(b.current?.value ?? 0) - Math.abs(a.current?.value ?? 0);
			}
		},
		[initialOrder],
	);

	const factorsData = useMemo(() => {
		const aggregateData = customObjectValuesFn(
			customObjectEntriesFn(investmentFactors).reduce<ReducedResultProps<typeof investmentFactors>>(
				(acc, [key, factorList]) => {
					factorList?.forEach((factor) => {
						if (factor.key === undefined) {
							acc.undefinedKey = {
								...acc.undefinedKey,
								[key]: factor,
								groupKey: "undefinedKey",
							};
						} else {
							acc[factor.key] = {
								...acc[factor.key],
								[key]: factor,
								groupKey: factor.key,
							};
						}
					});
					return acc;
				},
				{},
			),
		);
		return aggregateData;
	}, [customObjectEntriesFn, customObjectValuesFn, investmentFactors]);

	const calculatedData = useMemo(() => {
		const filteredData = filterBySignificant
			? factorsData?.filter(({ benchmark, current, proposal }) =>
					// Actually .relevant is not used because the model return 0 as value in those cases, we only check if all values are 0
					enhanced
						? [benchmark?.value ?? 0, current?.value ?? 0, proposal?.value ?? 0].some((e) => Math.abs(e) >= 0.01)
						: [benchmark?.value ?? 0, current?.value ?? 0].some((e) => Math.abs(e) >= 0.01),
				)
			: factorsData;
		return filteredData ? [...filteredData].sort((a, b) => customOrderFn(a, b)) : filteredData;
	}, [factorsData, customOrderFn, enhanced, filterBySignificant]);

	const graphLimits = useMemo(() => {
		const limits = (calculatedData ?? []).reduce(
			(a, c) => {
				return {
					maxValue: Math.max(a.maxValue, c.benchmark?.value ?? 0, c.proposal?.value ?? 0, c.current?.value ?? 0),
					minValue: Math.min(a.minValue, c.benchmark?.value ?? 0, c.proposal?.value ?? 0, c.current?.value ?? 0),
				};
			},
			{ maxValue: 0, minValue: 0 },
		);
		return {
			maxValue: roundCustomByStep(limits.maxValue, 0.5),
			minValue: roundCustomByStep(limits.minValue, 0.5),
		};
	}, [calculatedData]);

	const graphColumnMarkers = useMemo(() => {
		const markers = getGraphMarkers(graphLimits.maxValue, graphLimits.minValue, 0.5, "", 10);
		return markers;
	}, [graphLimits.maxValue, graphLimits.minValue]);

	const columns = useMemo<Array<TableColumn<NonNullable<Unpacked<typeof calculatedData>>>>>(
		() =>
			enhanced
				? [
						{
							minWidth: 480,
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.FACTORS"),
							content: ({ current }, cellProps) => (
								<TableDataCell {...cellProps}>
									<AutoTooltip
										severity="info"
										trigger={({ innerRef }) => (
											<span className="font-bold underline" ref={innerRef}>
												{current?.label}
											</span>
										)}
										position="bottom"
										align="startToEnd"
									>
										<TooltipContent>
											<Text type="Body/M/Book">
												{factorsDescription[current?.key as keyof typeof factorsDescription] ?? current?.key} &nbsp;
											</Text>
											<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("marketRegimeAnalysysTool")}>
												Read more
											</ActionText>
										</TooltipContent>
									</AutoTooltip>
								</TableDataCell>
							),
							sortFn: (a, b) => builtInSort(a.current?.label, b.current?.label),
							name: "label",
						},
						{
							...commonColumns.float(["current", "value"]),
							minWidth: 114,
							headerCellClassName: "whitespace-pre-line",
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.COMPOSITION_AT", {
								date: formatDate(ctx.portfolio.modificationDate, { omitYear: true }),
							}),
							name: "current",
						},
						{
							...commonColumns.float(["proposal", "value"]),
							minWidth: 114,
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.PROPOSAL"),
							name: "proposal",
						},
						{
							...commonColumns.float(["benchmark", "value"]),
							minWidth: 114,
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.BENCHMARK"),
							name: "benchmark",
						},
						{
							name: "graph",
							minWidth: 320,
							header: (headerProps) => (
								<Row {...headerProps} alignItems="center" justifyContent="space-between">
									<ForEach collection={graphColumnMarkers}>
										{(item) => <p className="text-[10px] text-[#667085] font-normal">{item.label}</p>}
									</ForEach>
								</Row>
							),
							content: ({ benchmark, current, proposal }, cellProps) => (
								<Row
									justifyContent="center"
									alignItems="center"
									{...cellProps}
									style={{ ...cellProps.style, height: defaultRowHeight }}
								>
									<BarGraphPCSvg
										className="w-full"
										options={{
											resize: true,
											marksLabels: false,
											markerStep: 0.5,
											scale: { max: graphLimits.maxValue, min: graphLimits.minValue },
										}}
										data={[
											{ value: current?.value ?? 0, color: current?.relevant ? "#005C8B" : "#ccc" },
											{ value: proposal?.value ?? 0, color: proposal?.relevant ? "#00AEEF" : "#ccc" },
											{ value: benchmark?.value ?? 0, color: benchmark?.relevant ? "#A0A7B6" : "#ccc" },
										]}
									/>
								</Row>
							),
						},
					]
				: [
						{
							minWidth: 480,
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.FACTORS"),
							content: ({ current }, cellProps) => (
								<TableDataCell {...cellProps}>
									<AutoTooltip
										severity="info"
										trigger={({ innerRef }) => (
											<span className="font-bold underline" ref={innerRef}>
												{current?.label}
											</span>
										)}
										position="bottom"
										align="startToEnd"
									>
										<TooltipContent>
											<Text type="Body/M/Book">
												{factorsDescription[current?.key as keyof typeof factorsDescription] ?? current?.key} &nbsp;
											</Text>
											<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("marketRegimeAnalysysTool")}>
												Read more
											</ActionText>
										</TooltipContent>
									</AutoTooltip>
								</TableDataCell>
							),
							sortFn: (a, b) => builtInSort(a.current?.label, b.current?.label),
							name: "label",
						},
						{
							...commonColumns.float(["current", "value"]),
							minWidth: 114,
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.CURRENT"),
							name: "current",
						},
						{
							...commonColumns.float(["benchmark", "value"]),
							minWidth: 114,
							header: t("PORTFOLIOFACTORSEXPOSURE.TABLE.BENCHMARK"),
							name: "benchmark",
						},
						{
							name: "graph",
							minWidth: 320,
							header: (headerProps) => (
								<Row {...headerProps} alignItems="center" justifyContent="space-between">
									<ForEach collection={graphColumnMarkers}>
										{(item) => <p className="text-[10px] text-[#667085] font-normal">{item.label}</p>}
									</ForEach>
								</Row>
							),
							content: ({ benchmark, current }, cellProps) => (
								<Row
									justifyContent="center"
									alignItems="center"
									{...cellProps}
									style={{ ...cellProps.style, height: defaultRowHeight }}
								>
									<BarGraphPCSvg
										className="w-full"
										options={{
											resize: true,
											marksLabels: false,
											markerStep: 0.5,
											scale: { max: graphLimits.maxValue, min: graphLimits.minValue },
										}}
										data={[
											{ value: current?.value ?? 0, color: current?.relevant ? "#005C8B" : "#ccc" },
											{ value: benchmark?.value ?? 0, color: benchmark?.relevant ? "#A0A7B6" : "#ccc" },
										]}
									/>
								</Row>
							),
						},
					],
		[
			factorsDescription,
			enhanced,
			graphColumnMarkers,
			graphLimits.maxValue,
			graphLimits.minValue,
			t,
			ctx.portfolio.modificationDate,
		],
	);

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIOFACTORSEXPOSURE.TITLE"),
			tooltip: (
				<TooltipContent>
					<Text type="Body/M/Book">
						Gain insights into the performance drivers of the asset class through factor exposure analysis. Discover how
						Sphere calculates its factors by accessing more information &nbsp;
					</Text>
					<ActionText type="Body/S/Book" onClick={() => Intercom.showArticle("marketRegimeAnalysysTool")}>
						here
					</ActionText>
				</TooltipContent>
			),
			actionHeader: calculatedData ? (
				<DropdownMenu
					Trigger={WidgetDownloadTrigger}
					extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioFactorExposure.downloadButton }}
					actions={[
						{
							icon: "xls",
							onClickAsync: async () => {
								await exportFactorsExposureRef.current(uuid, benchmarkId);
							},
							label: t("PORTFOLIOFACTORSEXPOSURE.DOWNLOAD_TITLE"),
							"data-qualifier": qualifier.widgets.portfolioFactorExposure.downloadItem,
						},
					]}
				/>
			) : null,
		}),
		[calculatedData, exportFactorsExposureRef, t, uuid, benchmarkId],
	);

	return (
		<div className="flex flex-col min-h-0">
			<div className="flex justify-between">
				<div className="flex gap-1">
					<p className="leading-loose">OrderBy:</p>
					{orderOptions
						.filter((o) => (enhanced ? true : !o.onlyEnhance) && o.show)
						.map((option) => (
							<Button
								key={option.value}
								size="x-small"
								palette={option.value === initialOrder ? "neutral" : "neutralOutline"}
								onClick={() => {
									setInitialOrder(option.value === initialOrder ? "Default" : option.value);
									// Changing the provider ordering resets any column sort (was a table remount via `key`).
									setOrderBy([]);
								}}
							>
								{option.label}
							</Button>
						))}
				</div>
				<Switch onChange={() => setFilterBySignificant(!filterBySignificant)} checked={filterBySignificant}>
					Significant values only
				</Switch>
			</div>
			<AutoSortHScrollTable
				columns={columns}
				rows={calculatedData}
				key={initialOrder}
				orderBy={orderBy}
				onOrderByChange={setOrderBy}
			/>
			<GraphLegend>
				<div className="legend-container light book" style={{ marginRight: "1rem" }}>
					<ColoredRectangle color="#005C8B" variant="vertical" />{" "}
					{`Composition ${formatDate(ctx.portfolio.modificationDate, { omitYear: true })}`}
				</div>
				{enhanced && (
					<div className="legend-container light book">
						<ColoredRectangle color="#00AEEF" variant="vertical" /> Proposal
					</div>
				)}
				<div className="legend-container light book">
					<ColoredRectangle color="#A0A7B6" variant="vertical" /> Benchmark
				</div>
			</GraphLegend>
		</div>
	);
};

export default withContext(PortfolioContext)(PortfolioFactorsExposure);
